import { ITag } from '@fluentui/react/lib/Pickers';
import React, { ChangeEvent, useContext, useState } from 'react';
import styled from 'styled-components/macro';
import Button from '../../shared/Button';
import * as Actions from '../../../store/actions';
import TagsEditorBasic from './TagsEditorBasic';
import MarkdownEditor from '../../shared/MarkdownEditor';
import Panel from '../../shared/Panel';
import { App } from '../../../app';
import { ResourceStrings } from '../../../ResourceStrings';
import GoodReportInstructions from '../Submission/GoodReportInstructions';
import { Icon } from '@fluentui/react/lib/Icon';
import { turndownService } from "../../../index";
import { isHtmlText } from '../../../utils/tools';
import { testFeedbackTitlePrefix } from '../../../constants/config';
import { maximumPostTitleLength, minimumPostDetailsLength, minimumPostTitleLength } from '../../../constants/validation';
import { TicketContext } from './TicketContext';
import { getSpaceConfig, instance } from '../../../config/serverConfig';
import PrivacyDisclosure from '../../App/PrivacyDisclosure';
import Flex from '../../shared/Flex';
import { forMinWidth } from '../../../theme/breakpoints';
import { PostType, removeReadOnly, Tag } from '../../../RootPost';

export interface TicketEditorFields {
  title: string;
  description: string;
  tags: Tag[];
}

export interface TicketUpdateFields {
  title: string;
  description: string;
  addedTags: string[];
  removedTags: string[];
}

export interface TicketEditorProps {
  trackingId: string;
  fields?: TicketEditorFields;
  formTitle?: string;
  guidelines?: string | JSX.Element;
  descriptionPlaceholderText?: string;
  minimumTitleLength?: number;
  maximumTitleLength?: number;
  minimumDescriptionLength?: number;

  forSuggestion: boolean;

  onCancel: () => void;
  onSave: (fields: TicketUpdateFields) => void;
}

const defaultDescriptionPlaceholder = "To increase our ability to provide a fix, please thoroughly explain the issue and add clear reproduction steps";

// To show a message 'X characters left' if less than those many are left
const charsLeftMessageThreshold = 20;

export default function TicketEditor(props: TicketEditorProps): JSX.Element {
  const [updatedTitle, setUpdatedTitle] = useState<string>(props.fields?.title ?? "");

  const [updatedDescription, setUpdatedDescription] = useState<string>(() => {
    let description = props.fields?.description ?? "";
    if (isHtmlText(description)) {
      description = turndownService.turndown(props.fields.description);
    }
    return description;
  });

  const ticketDetailsCtx = useContext(TicketContext);
  const [updatedTags, setUpdatedTags] = useState<ITag[]>(props.fields?.tags?.filter(removeReadOnly).map(x => ({ key: x.value, name: x.value } as ITag)) || []);
  const [areTagsValid, setAreTagsValid] = useState(true);
  const [isDirty, setIsDirty] = useState(false);

  const titleChanged = (e: ChangeEvent<HTMLInputElement>): void => {
    setUpdatedTitle(e.target.value)
    setIsDirty(true);
  }

  const descriptionChanged = (value: string): void => {
    setUpdatedDescription(value)
  }

  const tagListChanged = (items?: ITag[]): void => {
    setUpdatedTags(items);
    setIsDirty(true);
  }

  const tagsValidationStateChanged = (isValid: boolean, message: string): void => {
    setAreTagsValid(isValid);
  }

  const formatInfoHandler = (action: string): void => {
    Actions.setUsedFormatting(true);
    App.telemetry.postBusinessEvent("formatcommand-used", {
      command: action,
    });
  };

  const cancelClicked = (): void => {
    setIsDirty(false);
    props.onCancel();
  }

  const submitClicked = (): void => {
    setIsDirty(false);

    const originalTagValues = props.fields?.tags?.filter(removeReadOnly).map(x => x.value);
    const originalTitle = props.fields?.title;
    const originalDescription = turndownService.turndown(props.fields?.description);
    const updatedTagValues = updatedTags.map(x => x.key as string);

    const addedTags = updatedTagValues.filter(x => originalTagValues.indexOf(x) === -1);
    const removedTags = originalTagValues.filter(x => updatedTagValues.indexOf(x) === -1);

    props.onSave({
      title: (originalTitle !== updatedTitle) ? updatedTitle : null,
      description: (originalDescription !== updatedDescription) ? updatedDescription : null,
      addedTags,
      removedTags,
    });
  }

  const ignoreValidations = updatedTitle.startsWith(testFeedbackTitlePrefix);
  const minTitleLength = props.minimumTitleLength ?? minimumPostTitleLength;
  const maxTitleLength = props.maximumTitleLength ?? maximumPostTitleLength;
  const isTitleInvalid = !ignoreValidations && (!updatedTitle || updatedTitle.length < minTitleLength);

  const minDescriptionLength = props.minimumDescriptionLength ?? minimumPostDetailsLength;
  const isDescriptionShort = !ignoreValidations && (!updatedDescription || updatedDescription.length < minDescriptionLength);
  const isDescriptionSameAsTitle = !ignoreValidations && updatedDescription && updatedDescription.length >= minDescriptionLength && updatedDescription === updatedTitle;
  const isDescriptionInvalid = isDescriptionShort || isDescriptionSameAsTitle;
  let descriptionError: string;
  if (isDescriptionShort) {
    descriptionError = ResourceStrings.errorTextLongerThan(minDescriptionLength);
  }
  else if (isDescriptionSameAsTitle) {
    descriptionError = ResourceStrings.errorTitleAndDescriptionBeDifferent();
  }


  const ticket = ticketDetailsCtx?.ticketDetails?.ticket?.data;
  const spaceId = ticket?.spaceId;
  const spaceConfig = getSpaceConfig(spaceId);
  const reportConfig = ticket?.type === PostType.suggestion ? spaceConfig?.reportSuggestionConfig : spaceConfig?.reportProblemConfig;

  const isInvalid = !areTagsValid || isTitleInvalid || isDescriptionInvalid;

  return <Wrapper>
    <div className="top-heading top-level-text">
      <h1>{props.formTitle ?? (props.forSuggestion ? "Edit a feature request" : "Edit a problem")}</h1>
      
      {/*Not applicable for internal instance*/}
      {!instance.isInternal && <a
        href={props.forSuggestion ? "https://go.microsoft.com/fwlink/?linkid=2024240" : "https://go.microsoft.com/fwlink/?linkid=2106051"}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Icon iconName="UnknownSolid" />
        Learn about {props.forSuggestion ? "suggestions" : "problems"}
      </a>}
    </div>

    <GoodReportInstructions forSuggestion={props.forSuggestion} additionalGuidance={reportConfig?.additionalGuidance} />

    <EditorWrapper>
      <div>
        <label className="top-level-text">Title</label>
        <TitleWrapper className={isTitleInvalid ? "isInvalid" : ""}>
          <Icon iconName="SingleColumnEdit" />
          <input
            type="text"
            aria-label="Title"
            maxLength={maxTitleLength}
            value={updatedTitle}
            onChange={titleChanged}
          />
        </TitleWrapper>

        <div id="title-messages">
          {isDirty && isTitleInvalid && (
            <Error>{ResourceStrings.errorTextRangeBetween(minTitleLength, maxTitleLength)}</Error>
          )}

          {updatedTitle && (maxTitleLength - updatedTitle.length < charsLeftMessageThreshold) && (
            <Info>{ResourceStrings.charactersLeft(maxTitleLength - updatedTitle.length)}</Info>
          )}
        </div>
      </div>

      <div>
        <label className="top-level-text">Description</label>
        <Panel>
          <MarkdownEditor
            nodeTrackingId={props.trackingId}
            text={updatedDescription}
            ariaLabel={ResourceStrings.description()}
            ariaRequired={true}
            placeHolder={props.descriptionPlaceholderText ?? defaultDescriptionPlaceholder}
            relevantPersons={ticketDetailsCtx?.ticketDetails?.relevantPersons}
            formatInfoHandler={formatInfoHandler}
            beforePreview={(markdown: string) => markdown}
            textChanged={descriptionChanged}
            setDirty={() => setIsDirty(true)}
            isInvalid={isDirty && isDescriptionInvalid}
            notificationText={descriptionError}
          />
        </Panel>
      </div>

      <div>
        <label className="top-level-text">Topics</label>
        <TagsEditorBasic
          tags={updatedTags}
          tagListChanged={tagListChanged}
          validationStateChanged={tagsValidationStateChanged}
        />
      </div>

      <div className="buttons top-level-text">
        <PrivacyDisclosure
          includesNativeAttachments={false}
          disclosureLink={ResourceStrings.publicDisclosureProblem(App.GetPublicFeedbackSiteLink())}
          disclosure={ResourceStrings.publicDisclosureProblem(ResourceStrings.publicFeedbackSite())} />

        <Flex densityHor="normal" alignItems="flex-start">
          <Button
            buttonType="normal"
            iconName="Cancel"
            onClick={cancelClicked}
          >
            Cancel
          </Button>

          <Button
            iconName="Save"
            disabled={isInvalid || !isDirty}
            onClick={submitClicked}
          >
            Save
          </Button>
        </Flex>
      </div>
    </EditorWrapper>
  </Wrapper>
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  .top-heading {
    margin-bottom: 1rem;
  }

  .top-heading > a {
    display: inline-flex;

    i {
      margin-right: .4rem;
    }
  }

  .isInvalid {
    border: solid 1px var(--color-error);
  }

  >* + * {
    margin-top: 2rem;
  }

  .tag-editor {
    background-color: var(--color-surface);
  }
`;

const EditorWrapper = styled.div`
  display: flex;
  flex-direction: column;

  .markdown-toolbar {
    border-bottom: solid 1px var(--color-on-surface-lightest);
  }

  .html-preview {
    border-top: solid 1px var(--color-on-surface-lightest);
    padding: 1.2rem 2rem;
  }

  >* + * {
      margin-top: 2rem;
  }

  .buttons {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-top: 2.5rem;

    >* + * {
      margin-top: 1rem;
    }

    ${forMinWidth.md`
      flex-direction: row;

      >* + * {
        margin-left: 3rem;
        margin-top: 0;
      }
    `}

    button {
      font-size: 1.6rem;
      padding: 0 2.5rem;
      min-height: 3.5rem;
    }
  }

  textarea {
    margin: 0;
    padding: 1.2rem 2rem;
  }

  label {
    display: block;
    margin-bottom: .6rem;
    font-weight: bold;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: var(--color-surface);
  border: solid 1px var(--color-surface);
  box-shadow: 0px 0.3px 0.9px rgba(0,0,0,0.108), 0px 1.6px 3.6px rgba(0,0,0,0.132);
  border-radius: .2rem;

  i {
    margin: 0 .5rem 0 1rem;
  }

input {
    border: none;
    width: 100%;
    margin: .2rem;
    padding: 1.4rem 1rem;
    font-size: 1.8rem;
  }
`;

const Error = styled.div`
    color: var(--color-error);
    padding: 0.5rem 1rem .5rem 1.2rem;
`;

const Info = styled(Error)`
    color: var(--color-on-surface-light);
`;
